/**
 * Returns the currency symbol for a given ISO 4217 currency code.
 * e.g. "USD" → "$", "EUR" → "€", "GBP" → "£", "INR" → "₹"
 */
export function getCurrencySymbol(currencyCode: string): string {
  try {
    const parts = new Intl.NumberFormat("en", {
      style: "currency",
      currency: currencyCode,
    }).formatToParts(0);
    return parts.find((p) => p.type === "currency")?.value ?? currencyCode;
  } catch {
    return currencyCode;
  }
}

/**
 * Formats an amount with the given ISO 4217 currency code.
 * e.g. formatCurrencyAmount(9.99, "USD") → "$9.99"
 */
export function formatCurrencyAmount(amount: number, currencyCode: string): string {
  try {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency: currencyCode,
    }).format(amount);
  } catch {
    return `${currencyCode} ${amount.toFixed(2)}`;
  }
}

// ─── Active (admin-selected) currency ────────────────────────────────────────
// Set once the platform currency is loaded (AuthContext), so modules that
// aren't React components (receipts, printers, reports) can use it too.
let activeCurrency = "USD";

export function setActiveCurrency(currencyCode: string): void {
  if (currencyCode) activeCurrency = currencyCode.toUpperCase();
}

export function getActiveCurrency(): string {
  return activeCurrency;
}

/** Symbol of the admin-selected currency, e.g. "CA$". */
export function cs(): string {
  return getCurrencySymbol(activeCurrency);
}

/** Amount with the admin-selected currency symbol, e.g. money(12.5) → "CA$12.50". */
export function money(amount: number, decimals = 2): string {
  return `${cs()}${Number(amount || 0).toFixed(decimals)}`;
}
